import { apiClient } from './client'
import type { ApiResponse } from './client'
import type {
  TouchPointCreateDto,
  TouchPointViewDto,
  TouchPointUpdateDto,
  EmailSendRuleDto,
  MeetingRecordingUrlDto,
  MeetingTranscriptUrlDto,
} from '@/types/touchPoint'

export type TouchPointSchedulePatchDto = {
  StartDate: string // ISO 8601 DateTimeOffset - for Email touchpoints, this is the scheduled date; for Meeting touchpoints, this is the start time
  EndDate?: string // ISO 8601 DateTimeOffset - optional, only used for Meeting touchpoints
  ETag: string
}

export type TouchPointRulePatchDto = {
  Rule: EmailSendRuleDto
  ETag: string
}

export const touchPointsApi = {
  // Get all touchpoints for a specific web (optionally filtered by types)
  getTouchPointsByWeb: (workspaceId: string, webId: string, types?: string[]): Promise<ApiResponse<TouchPointViewDto[]>> => {
    const url = types && types.length > 0
      ? `/api/Workspaces/${workspaceId}/Webs/${webId}/TouchPoints?types=${types.join(',')}`
      : `/api/Workspaces/${workspaceId}/Webs/${webId}/TouchPoints`
    return apiClient.get(url)
  },

  // Get campaign-level touchpoints by default; optionally include web-level touchpoints and filter by types
  getTouchPointsByCampaign: (workspaceId: string, campaignId: string, types?: string[], includeWebs = false): Promise<ApiResponse<TouchPointViewDto[]>> => {
    const baseUrl = `/api/Workspaces/${workspaceId}/Campaigns/${campaignId}/TouchPoints`
    const query = [
      types && types.length > 0 ? `types=${types.join(',')}` : undefined,
      includeWebs ? 'includeWebs=true' : undefined,
    ].filter(Boolean).join('&')
    const url = query ? `${baseUrl}?${query}` : baseUrl
    return apiClient.get(url)
  },

  // Create a new touchpoint (with optional file upload for meetings)
  createTouchPoint: (workspaceId: string, dto: TouchPointCreateDto, file?: File): Promise<ApiResponse<TouchPointViewDto>> => {
    if (file) {
      const formData = new FormData()
      formData.append('touchPointData', JSON.stringify(dto))
      formData.append('file', file)
      return apiClient.post(`/api/Workspaces/${workspaceId}/TouchPoints`, formData)
    }
    return apiClient.post(`/api/Workspaces/${workspaceId}/TouchPoints`, dto)
  },

  // Update an existing touchpoint (with optional file upload for meetings)
  updateTouchPoint: (workspaceId: string, id: string, dto: TouchPointUpdateDto, file?: File): Promise<ApiResponse<TouchPointViewDto>> => {
    if (file) {
      const formData = new FormData()
      formData.append('touchPointData', JSON.stringify(dto))
      formData.append('file', file)
      return apiClient.put(`/api/Workspaces/${workspaceId}/TouchPoints/${id}`, formData)
    }
    return apiClient.put(`/api/Workspaces/${workspaceId}/TouchPoints/${id}`, dto)
  },

  // Patch touchpoint schedule (for calendar drag-and-drop on non-email touchpoints)
  patchSchedule: (workspaceId: string, id: string, dto: TouchPointSchedulePatchDto): Promise<ApiResponse<TouchPointViewDto>> =>
    apiClient.request(`/api/Workspaces/${workspaceId}/TouchPoints/${id}/Schedule`, { method: 'PATCH', body: dto }),

  // Patch email touchpoint rule (replaces the inline send-time concept for email touchpoints)
  patchRule: (workspaceId: string, id: string, dto: TouchPointRulePatchDto): Promise<ApiResponse<TouchPointViewDto>> =>
    apiClient.request(`/api/Workspaces/${workspaceId}/TouchPoints/${id}/Rule`, { method: 'PATCH', body: dto }),

  // Delete a touchpoint
  deleteTouchPoint: (workspaceId: string, id: string): Promise<ApiResponse<null>> =>
    apiClient.delete(`/api/Workspaces/${workspaceId}/TouchPoints/${id}`),

  // Fetch a fresh short-lived SAS URL to play back a meeting's Webster recording. Called on demand
  // when the user hits play, so the URL is always fresh. 404 (no data) when there is no recording.
  getRecording: (workspaceId: string, id: string): Promise<ApiResponse<MeetingRecordingUrlDto>> =>
    apiClient.get(`/api/Workspaces/${workspaceId}/TouchPoints/${id}/Recording`),

  // Fetch a single touchpoint by id (ORB-436). Self-sufficient source for the meeting page on a cold
  // load / deep link, where the list isn't in memory. Returns the same shape as one list item.
  getTouchPoint: (workspaceId: string, id: string): Promise<ApiResponse<TouchPointViewDto>> =>
    apiClient.get(`/api/Workspaces/${workspaceId}/TouchPoints/${id}`),

  // Fetch a fresh short-lived SAS URL to the meeting's transcript JSON (ORB-436). Minted on demand so a
  // deep-linked/reloaded meeting page never depends on the stale SAS in the list payload. 404 when none.
  getTranscriptUrl: (workspaceId: string, id: string): Promise<ApiResponse<MeetingTranscriptUrlDto>> =>
    apiClient.get(`/api/Workspaces/${workspaceId}/TouchPoints/${id}/Transcript`),

  // Pre-send Start-over (ORB-378): clears the staged blast so the activation can be edited + re-sent.
  // 409 when no blast is staged for the activation, or when sending has already started.
  startOver: (workspaceId: string, id: string): Promise<ApiResponse<TouchPointViewDto>> =>
    apiClient.post(`/api/Workspaces/${workspaceId}/TouchPoints/${id}/StartOver`, {}),

  // Re-arm a started-over activation so the dispatch worker stages it again at its send time (ORB-378).
  reschedule: (workspaceId: string, id: string): Promise<ApiResponse<TouchPointViewDto>> =>
    apiClient.post(`/api/Workspaces/${workspaceId}/TouchPoints/${id}/Reschedule`, {})
}
